import { Calendar, Clock, Flag01, Lock01, User01 } from '@untitledui/icons';

import { DateTimeValueLabel } from '@/lib/crm/components/properties/labels/DateTimeValueLabel';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import type { Note } from '@/lib/crm/record/notes/notes.types';

import { OverviewFieldRow } from '../components/OverviewFieldRow';
import { MemoPrivacyToggle } from '../notes/MemoPrivacyToggle';
import { NoteStageLink } from '../notes/NoteCard.subcomponents';
import { type MemoEntityType, memoPrivacy } from '../notes/notes.utils';
import { TaskAssigneeField } from './TaskAssigneeField';
import { TaskDueDateField } from './TaskDueDateField';

/**
 * Every Task Overview value is single-line, but their boxes differ in height —
 * Status and Assignee are bordered, padded pills, the dates are bare text inside
 * a button wrapper. OverviewFieldRow's default `items-start` top-aligns those
 * boxes, so each value's TEXT landed on a different optical line from its label
 * (measured +3 to +5.3px). Centering aligns text to text regardless of box
 * height. `cx` is tailwind-merge, so this cleanly overrides the shared row's
 * `items-start` without editing the shared component (other consumers of
 * OverviewFieldRow have multi-line values and still want top alignment).
 *
 * Deliberately NO min-height reservation: reserving the due-date editor's
 * 32px `xs`-control footprint kept the row from growing on edit entry, but
 * made every read-state row visibly too tall (product call: compact read
 * state wins over the one-row 8px grow when the date editor opens).
 */
const OVERVIEW_ROW_CLASS = 'items-center';

// Stable no-op for the read-only Visibility row (the control is disabled then).
const NOOP_PRIVACY_CHANGE = () => {};

interface TaskOverviewFieldsProps {
  orgId: number;
  entityType: MemoEntityType;
  task: Note;
  dueDateSchema: PropertyTypeSchema | undefined;
  onStageChange?: (
    entityId: string,
    newStageEntityId: string | null
  ) => Promise<void>;
  onSaveDue?: (newDate: string | null) => Promise<void>;
  onSaveAssignee?: (targetIds: string[]) => Promise<void>;
  isAssigneeMutationPending: boolean;
  /**
   * Flip the task's visibility (`true` = shared with the org). Withheld
   * (undefined) when the viewer is neither owner nor creator — the same gate
   * the backend's privacy toggle enforces — which renders the row read-only.
   */
  onPrivacyChange?: (isShared: boolean) => void;
  isPrivacyMutationPending?: boolean;
}

/** The "Overview" section body: status/due-date/assignee/last-interaction rows. */
export function TaskOverviewFields({
  orgId,
  entityType,
  task,
  dueDateSchema,
  onStageChange,
  onSaveDue,
  onSaveAssignee,
  isAssigneeMutationPending,
  onPrivacyChange,
  isPrivacyMutationPending = false,
}: TaskOverviewFieldsProps) {
  const privacy = memoPrivacy(task);
  return (
    <div className='flex flex-col gap-2 pt-1'>
      <OverviewFieldRow
        icon={Flag01}
        label='Status'
        rowClassName={OVERVIEW_ROW_CLASS}
      >
        <NoteStageLink
          orgId={orgId}
          entityType={entityType}
          stageEntityId={task.stage_entity_id}
          noteEntityId={task.entity_id}
          onStageChange={onStageChange}
        />
      </OverviewFieldRow>
      <OverviewFieldRow
        icon={Calendar}
        label='Due date'
        rowClassName={OVERVIEW_ROW_CLASS}
      >
        <TaskDueDateField
          date={task.date}
          datetime={task.datetime}
          dueDateSchema={onSaveDue ? dueDateSchema : undefined}
          onSave={onSaveDue}
        />
      </OverviewFieldRow>
      <OverviewFieldRow
        icon={User01}
        label='Assignee'
        rowClassName={OVERVIEW_ROW_CLASS}
      >
        <TaskAssigneeField
          ownerEntityId={task.owner_entity_id}
          orgId={orgId}
          onSave={onSaveAssignee}
          mutationPending={isAssigneeMutationPending}
        />
      </OverviewFieldRow>
      <OverviewFieldRow
        icon={Lock01}
        label='Visibility'
        rowClassName={OVERVIEW_ROW_CLASS}
      >
        {/* Tasks carry the same org/owner visibility model as notes (the
            backend's privacy toggle is memo-kind generic). Read-only for a
            viewer who is neither owner nor creator — the dropdown stays so the
            value reads the same either way. */}
        <MemoPrivacyToggle
          variant='labeled'
          privacy={privacy}
          onChange={onPrivacyChange ?? NOOP_PRIVACY_CHANGE}
          isDisabled={onPrivacyChange === undefined || isPrivacyMutationPending}
        />
      </OverviewFieldRow>
      <OverviewFieldRow
        icon={Clock}
        label='Last interaction'
        rowClassName={OVERVIEW_ROW_CLASS}
      >
        {/* Default `sm` matches the row's label size — see TaskDueDateField. */}
        <DateTimeValueLabel value={task.updated_at || null} />
      </OverviewFieldRow>
    </div>
  );
}
